import { useState, useEffect } from 'react'
import DashboardLayout from '../../common/DashboardLayout'
import { Icon1, Icon2, User2, course02 } from '../../imagepath'
import { Link } from 'react-router-dom'
import AdminServices from "../../../services/AdminServices";
import './AdminDashboard.css';

const AdminDashboard = () => {
    const [schools, setSchools] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [dashboardStats, setDashboardStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [teachersRes, schoolsRes, statsRes] = await Promise.all([
                    AdminServices.getAllTeachers(),
                    AdminServices.getAllSchools(),
                    AdminServices.getDashboardStats()
                ]);

                setTeachers(teachersRes?.teachers || []);
                setSchools(schoolsRes?.schools || []);
                setDashboardStats(statsRes?.stats || null);
            } catch (error) {
                console.error("Error fetching dashboard data:", error);
                console.error("Error details:", {
                    message: error?.message,
                    response: error?.response?.data,
                    status: error?.response?.status,
                });
                setTeachers([]);
                setSchools([]);
                setDashboardStats(null);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);
    if (loading) {
        return (
            <DashboardLayout
                role="admin"
                layoutType="topnav"
                activeMenu="Dashboard"
                showBreadcrumbs={false}
            >
                <div className="dashboard-loading">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    // Use dashboardStats if available, otherwise fallback to basic counts
    const stats = dashboardStats || {
        totalSchools: schools.length,
        totalTeachers: teachers.length,
        teachersCompletedCourses: 0,
        completionPercentage: 0,
        totalCompletions: 0,
        schoolsCompletionPercentage: 0,
        recentCompletions: 0,
        mostActiveSchools: [],
        leastActiveSchools: []
    };

    console.log("Dashboard stats:", stats);

    return (
        <DashboardLayout
            role="admin"
            layoutType="topnav"
            activeMenu="Dashboard"
            showBreadcrumbs={false}
        >
            <div className="admin-dashboard-container">
                {/* Recurring revenue — sysadmin only (mrr/arr present in stats only for admins) */}
                {(stats.mrr !== undefined || stats.arr !== undefined) && (
                    <div className="row g-4 mb-4">
                        <div className="col-lg-6 col-md-6">
                            <div className="dashboard-stat-card stat-card-success">
                                <div className="stat-card-icon">
                                    <i className="bx bx-dollar-circle"></i>
                                </div>
                                <div className="stat-card-content">
                                    <h3 className="stat-card-value">
                                        ${Number(stats.mrr || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </h3>
                                    <p className="stat-card-label">Monthly Recurring Revenue (MRR)</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6 col-md-6">
                            <div className="dashboard-stat-card stat-card-primary">
                                <div className="stat-card-icon">
                                    <i className="bx bx-line-chart"></i>
                                </div>
                                <div className="stat-card-content">
                                    <h3 className="stat-card-value">
                                        ${Number(stats.arr || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </h3>
                                    <p className="stat-card-label">Annual Recurring Revenue (ARR)</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Main Stats Cards */}
                <div className="row g-4 mb-4">
                    <div className="col-lg-3 col-md-6">
                        <div className="dashboard-stat-card stat-card-primary">
                            <div className="stat-card-icon">
                                <i className="bx bx-building"></i>
                            </div>
                            <div className="stat-card-content">
                                <h3 className="stat-card-value">{stats.totalSchools || schools.length}</h3>
                                <p className="stat-card-label">Total Schools</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                        <div className="dashboard-stat-card stat-card-success">
                            <div className="stat-card-icon">
                                <i className="bx bx-user"></i>
                            </div>
                            <div className="stat-card-content">
                                <h3 className="stat-card-value">{stats.totalTeachers || teachers.length}</h3>
                                <p className="stat-card-label">Total Teachers</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                        <div className="dashboard-stat-card stat-card-info">
                            <div className="stat-card-icon">
                                <i className="bx bx-check-circle"></i>
                            </div>
                            <div className="stat-card-content">
                                <h3 className="stat-card-value">{stats.teachersCompletedCourses || 0}</h3>
                                <p className="stat-card-label">Teachers Completed Courses</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                        <div className="dashboard-stat-card stat-card-warning">
                            <div className="stat-card-icon">
                                <i className="bx bx-trophy"></i>
                            </div>
                            <div className="stat-card-content">
                                <h3 className="stat-card-value">{stats.totalCompletions || 0}</h3>
                                <p className="stat-card-label">Total Course Completions</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Progress Cards */}
                <div className="row g-4 mb-4">
                    <div className="col-lg-6 col-md-6">
                        <div className="dashboard-progress-card">
                            <div className="progress-card-header">
                                <h4><i className="bx bx-chart"></i> Teacher Completion Rate</h4>
                            </div>
                            <div className="progress-card-body">
                                <div className="progress-stat">
                                    <span className="progress-value">{stats.completionPercentage || 0}%</span>
                                    <span className="progress-label">
                                        {stats.teachersCompletedCourses || 0} of {stats.totalTeachers || teachers.length} teachers
                                    </span>
                                </div>
                                <div className="progress-bar-container">
                                    <div
                                        className="progress-bar-fill"
                                        style={{ width: `${stats.completionPercentage || 0}%` }}
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6 col-md-6">
                        <div className="dashboard-progress-card">
                            <div className="progress-card-header">
                                <h4><i className="bx bx-school"></i> Schools Completion Rate</h4>
                            </div>
                            <div className="progress-card-body">
                                <div className="progress-stat">
                                    <span className="progress-value">{stats.schoolsCompletionPercentage || 0}%</span>
                                    <span className="progress-label">
                                        {stats.mostActiveSchools?.length || 0} schools with completions
                                    </span>
                                </div>
                                <div className="progress-bar-container">
                                    <div
                                        className="progress-bar-fill progress-bar-success"
                                        style={{ width: `${stats.schoolsCompletionPercentage || 0}%` }}
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Activity Cards */}
                <div className="row g-4">
                    <div className="col-lg-6">
                        <div className="dashboard-activity-card">
                            <div className="activity-card-header">
                                <h4><i className="bx bx-trending-up"></i> Most Active Schools</h4>
                                <span className="activity-badge">{stats.mostActiveSchools?.length || 0} schools</span>
                            </div>
                            <div className="activity-card-body">
                                {stats.mostActiveSchools && stats.mostActiveSchools.length > 0 ? (
                                    <ul className="activity-list">
                                        {stats.mostActiveSchools.map((school, index) => (
                                            <li key={school.schoolId || index} className="activity-item">
                                                <div className="activity-rank">{index + 1}</div>
                                                <div className="activity-info">
                                                    <div className="activity-name">{school.schoolName}</div>
                                                    <div className="activity-meta">
                                                        {school.completions} completions • {school.teacherCount} teachers
                                                    </div>
                                                </div>
                                                <div className="activity-badge-small">
                                                    <i className="bx bx-check-circle"></i>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="activity-empty">
                                        <i className="bx bx-info-circle"></i>
                                        <p>No active schools yet</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="dashboard-activity-card">
                            <div className="activity-card-header">
                                <h4><i className="bx bx-trending-down"></i> Least Active Schools</h4>
                                <span className="activity-badge activity-badge-warning">
                                    {stats.leastActiveSchools?.length || 0} schools
                                </span>
                            </div>
                            <div className="activity-card-body">
                                {stats.leastActiveSchools && stats.leastActiveSchools.length > 0 ? (
                                    <ul className="activity-list">
                                        {stats.leastActiveSchools.map((school, index) => (
                                            <li key={school.schoolId || index} className="activity-item">
                                                <div className="activity-rank activity-rank-warning">{index + 1}</div>
                                                <div className="activity-info">
                                                    <div className="activity-name">{school.schoolName}</div>
                                                    <div className="activity-meta">
                                                        {school.teacherCount} teachers • {school.completions} completions
                                                    </div>
                                                </div>
                                                <div className="activity-badge-small activity-badge-small-warning">
                                                    <i className="bx bx-time"></i>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="activity-empty">
                                        <i className="bx bx-check-circle"></i>
                                        <p>All schools are active!</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Recent Activity Card */}
                <div className="row g-4 mt-2">
                    <div className="col-12">
                        <div className="dashboard-recent-card">
                            <div className="recent-card-header">
                                <h4><i className="bx bx-calendar"></i> Recent Activity (Last 30 Days)</h4>
                            </div>
                            <div className="recent-card-body">
                                <div className="recent-stat">
                                    <div className="recent-stat-item">
                                        <i className="bx bx-check-circle text-success"></i>
                                        <div>
                                            <span className="recent-stat-value">{stats.recentCompletions || 0}</span>
                                            <span className="recent-stat-label">Course Completions</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    )
}

export default AdminDashboard
